import {
  CheckboxGroupInput,
  Datagrid,
  Edit,
  FunctionField,
  List,
  Pagination,
  Resource,
  TextField,
  useGetList,
  useRecordContext,
} from "react-admin";
import AutovioEmployeeIcon from "@mui/icons-material/Engineering";
import { AvatarField } from "../fields/AvatarField";
import { Avatar, Box, Card } from "@mui/material";
import { BackofficeListTitle } from "../misc/BackofficeListTitle";
import { Row } from "../misc/Row";
import { PageTitle } from "../misc/PageTitle";
import { AutovioEmployee } from "../providers/autovioEmployeesProvider";
import { useFormContext } from "react-hook-form";
import { SaveButton2 } from "../buttons/SaveButton2";
import { Column } from "../misc/Column";
import { Form2 } from "../misc/Form2";
import { overviewCardStyle } from "../misc/backofficeTheme";
import { BackButton } from "../buttons/BackButton";
import { SearchFilterInput } from "../inputs/SearchFilterInput";

const filters = [<SearchFilterInput key="q" source="q" alwaysOn resettable />];

const EmployeePagination = () => <Pagination rowsPerPageOptions={[10, 25, 50, 100]} />;

function AutovioEmployeeList() {
  return (
    <List
      title={<BackofficeListTitle />}
      filters={filters}
      sort={{ field: "displayName", order: "ASC" }}
      pagination={<EmployeePagination />}
      exporter={false}
    >
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <FunctionField label="Name" source="displayName" render={_renderAvatarAndName} />
        <FunctionField label="Rollen" render={(record) => record?.roles.join(", ")} />
      </Datagrid>
    </List>
  );
}

function _renderAvatarAndName(autovioEmployee: AutovioEmployee) {
  return (
    <Box sx={{ display: "flex", gap: "15px", alignItems: "center", marginLeft: "16px" }}>
      <Avatar src={autovioEmployee.avatarUrl} />
      <TextField source="displayName" />
    </Box>
  );
}

const ROLES_CHOICES = [
  { id: "admin", name: "AUTOVIO Admin (admin)" },
  { id: "partnerSuccess", name: "AUTOVIO Customer Success Fahrlehrer (partnerSuccess)" },
  { id: "studentSales", name: "AUTOVIO Customer Success Fahrschüler (studentSales)" },
  { id: "drivingSchoolManager", name: "Fahrschulleiter (drivingSchoolManager)" },
  { id: "drivingSchoolAnalyst", name: "Fahrschulanalyst (drivingSchoolAnalyst)" },
  { id: "drivingSchoolAccountant", name: "Fahrschulbuchhalter (drivingSchoolAccountant)" },
  { id: "drivingSchoolCustomerService", name: "Fahrschule Kundenservice (drivingSchoolCustomerService)" },
  { id: "instructor", name: "Fahrlehrer (instructor)" },
  { id: "student", name: "Fahrschüler (student)" },
  { id: "appTester", name: "AUTOVIO Tester (appTester)" },
];

function AutovioEmployeeDetails() {
  return (
    <Edit
      title={<AutovioEmployeeHeader />}
      sx={{
        ...overviewCardStyle,
        "& .MuiPaper-root": {
          borderRadius: 0,
        },
      }}
      mutationMode="pessimistic"
      redirect={false}
    >
      <AutovioEmployeeForm />
    </Edit>
  );
}

function AutovioEmployeeHeader() {
  const autovioEmployee = useRecordContext<AutovioEmployee>();

  return (
    <Card sx={{ ...overviewCardStyle, mb: 3 }}>
      <Column spacing={2} alignItems="start">
        <BackButton />
        <Row spacing="20px" alignItems="center">
          <AvatarField source="avatarUrl" sx={{ width: "60px", height: "60px" }} />
          <Box sx={{ ml: "20px", display: "flex", flexDirection: "column" }}>
            {autovioEmployee && <PageTitle>{autovioEmployee.displayName}</PageTitle>}
          </Box>
        </Row>
      </Column>
    </Card>
  );
}

function AutovioEmployeeForm() {
  const autovioEmployee = useRecordContext<AutovioEmployee>();

  if (!autovioEmployee) {
    return null;
  }

  return (
    <Form2 warnWhenUnsavedChanges>
      <Row gap="30px">
        <RolesInput />
        <EntitledForDrivingSchoolsBox />
      </Row>
      <SaveButton2 />
    </Form2>
  );
}

export function RolesInput({ label = "Rollen" }: { label?: string }) {
  return <CheckboxGroupInput label={label} source="roles" choices={ROLES_CHOICES} row={false} />;
}

export function EntitledForDrivingSchoolsBox() {
  const { watch } = useFormContext<{ roles: Array<string> }>();
  const roles = watch("roles") ?? [];
  const { data: drivingSchools } = useGetList("drivingSchools", {
    pagination: { page: 1, perPage: 9999 },
    sort: { field: "name", order: "ASC" },
  });

  if (
    !(roles.some((it) => it !== "drivingSchoolManager" && it.startsWith("drivingSchool")) && drivingSchools?.length)
  ) {
    return null;
  }

  return (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 0.5, px: "20px" }}>
      <CheckboxGroupInput
        label="hat Zugriff auf"
        source="entitledForDrivingSchools"
        choices={drivingSchools}
        row={false}
      />
    </Box>
  );
}

export const autovioEmployeesResource = (
  <Resource
    key="autovioEmployees"
    name="autovioEmployees"
    icon={AutovioEmployeeIcon}
    list={AutovioEmployeeList}
    edit={AutovioEmployeeDetails}
    options={{ label: "AUTOVIO Mitarbeiter" }}
    recordRepresentation={(autovioEmployee) => autovioEmployee.name}
  />
);
